// Geolocation detection with multiple fallback strategies

const GEO_KEY = "decision-os-geo";

export interface GeoData {
  country: string;
  countryCode: string;
  city: string;
  region: string;
  currency: string;
  detectedAt: string;
  overridden: boolean;
  detectionMethod: "ip-api" | "browser-locale" | "timezone" | "manual" | "cached";
}

const COUNTRY_CURRENCY: Record<string, string> = {
  US: "USD", GB: "GBP", CA: "CAD", AU: "AUD", DE: "EUR", FR: "EUR",
  JP: "JPY", IN: "INR", AE: "AED", SG: "SGD", BR: "BRL", MX: "MXN",
  NL: "EUR", SE: "SEK", ES: "EUR", IT: "EUR", KR: "KRW", NZ: "NZD",
  IE: "EUR", AT: "EUR", BE: "EUR", FI: "EUR", PT: "EUR", PL: "PLN",
  CH: "CHF", NO: "NOK", DK: "DKK", PH: "PHP", TH: "THB", MY: "MYR",
  ZA: "ZAR", NG: "NGN", EG: "EGP", SA: "SAR", QA: "QAR",
  HK: "HKD", TW: "TWD", ID: "IDR", VN: "VND", CL: "CLP", CO: "COP",
  AR: "ARS", PE: "PEN", RO: "RON", CZ: "CZK", HU: "HUF", IL: "ILS",
  TR: "TRY", PK: "PKR", BD: "BDT", LK: "LKR", KE: "KES",
};

// Timezone-to-country mapping for fallback detection
const TZ_COUNTRY: Record<string, { code: string; name: string; city: string }> = {
  "America/New_York": { code: "US", name: "United States", city: "New York" },
  "America/Chicago": { code: "US", name: "United States", city: "Chicago" },
  "America/Denver": { code: "US", name: "United States", city: "Denver" },
  "America/Los_Angeles": { code: "US", name: "United States", city: "Los Angeles" },
  "America/Toronto": { code: "CA", name: "Canada", city: "Toronto" },
  "America/Vancouver": { code: "CA", name: "Canada", city: "Vancouver" },
  "America/Montreal": { code: "CA", name: "Canada", city: "Montreal" },
  "Europe/London": { code: "GB", name: "United Kingdom", city: "London" },
  "Europe/Berlin": { code: "DE", name: "Germany", city: "Berlin" },
  "Europe/Paris": { code: "FR", name: "France", city: "Paris" },
  "Europe/Amsterdam": { code: "NL", name: "Netherlands", city: "Amsterdam" },
  "Europe/Madrid": { code: "ES", name: "Spain", city: "Madrid" },
  "Europe/Rome": { code: "IT", name: "Italy", city: "Rome" },
  "Europe/Stockholm": { code: "SE", name: "Sweden", city: "Stockholm" },
  "Asia/Tokyo": { code: "JP", name: "Japan", city: "Tokyo" },
  "Asia/Kolkata": { code: "IN", name: "India", city: "Mumbai" },
  "Asia/Dubai": { code: "AE", name: "UAE", city: "Dubai" },
  "Asia/Singapore": { code: "SG", name: "Singapore", city: "Singapore" },
  "Asia/Seoul": { code: "KR", name: "South Korea", city: "Seoul" },
  "Asia/Hong_Kong": { code: "HK", name: "Hong Kong", city: "Hong Kong" },
  "Australia/Sydney": { code: "AU", name: "Australia", city: "Sydney" },
  "Australia/Melbourne": { code: "AU", name: "Australia", city: "Melbourne" },
  "Pacific/Auckland": { code: "NZ", name: "New Zealand", city: "Auckland" },
  "America/Sao_Paulo": { code: "BR", name: "Brazil", city: "Sao Paulo" },
  "America/Mexico_City": { code: "MX", name: "Mexico", city: "Mexico City" },
  "Asia/Riyadh": { code: "SA", name: "Saudi Arabia", city: "Riyadh" },
  "Africa/Johannesburg": { code: "ZA", name: "South Africa", city: "Johannesburg" },
};

// Browser locale to country fallback
const LOCALE_COUNTRY: Record<string, { code: string; name: string }> = {
  "en-US": { code: "US", name: "United States" },
  "en-GB": { code: "GB", name: "United Kingdom" },
  "en-CA": { code: "CA", name: "Canada" },
  "en-AU": { code: "AU", name: "Australia" },
  "en-NZ": { code: "NZ", name: "New Zealand" },
  "en-IN": { code: "IN", name: "India" },
  "fr-FR": { code: "FR", name: "France" },
  "fr-CA": { code: "CA", name: "Canada" },
  "de-DE": { code: "DE", name: "Germany" },
  "de-AT": { code: "AT", name: "Austria" },
  "de-CH": { code: "CH", name: "Switzerland" },
  "es-ES": { code: "ES", name: "Spain" },
  "es-MX": { code: "MX", name: "Mexico" },
  "it-IT": { code: "IT", name: "Italy" },
  "ja-JP": { code: "JP", name: "Japan" },
  "ko-KR": { code: "KR", name: "South Korea" },
  "pt-BR": { code: "BR", name: "Brazil" },
  "nl-NL": { code: "NL", name: "Netherlands" },
  "sv-SE": { code: "SE", name: "Sweden" },
  "ar-AE": { code: "AE", name: "UAE" },
  "ar-SA": { code: "SA", name: "Saudi Arabia" },
  "zh-CN": { code: "CN", name: "China" },
  "zh-TW": { code: "TW", name: "Taiwan" },
  "zh-HK": { code: "HK", name: "Hong Kong" },
};

export function loadGeo(): GeoData | null {
  try {
    const raw = localStorage.getItem(GEO_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

export function saveGeo(geo: GeoData): void {
  try { localStorage.setItem(GEO_KEY, JSON.stringify(geo)); } catch {}
}

export function overrideGeo(countryCode: string, country: string): void {
  const existing = loadGeo();
  const geo: GeoData = {
    country,
    countryCode,
    city: existing?.city || "",
    region: existing?.region || "",
    currency: COUNTRY_CURRENCY[countryCode] || "USD",
    detectedAt: existing?.detectedAt || new Date().toISOString(),
    overridden: true,
    detectionMethod: "manual",
  };
  saveGeo(geo);
}

// Detect from timezone as first fallback
function detectFromTimezone(): GeoData | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const match = TZ_COUNTRY[tz];
    if (match) {
      return {
        country: match.name,
        countryCode: match.code,
        city: match.city,
        region: "",
        currency: COUNTRY_CURRENCY[match.code] || "USD",
        detectedAt: new Date().toISOString(),
        overridden: false,
        detectionMethod: "timezone",
      };
    }
  } catch {}
  return null;
}

// Detect from browser locale as second fallback
function detectFromLocale(): GeoData | null {
  try {
    const locale = navigator.language || (navigator as any).userLanguage;
    if (locale) {
      const match = LOCALE_COUNTRY[locale];
      if (match) {
        return {
          country: match.name,
          countryCode: match.code,
          city: "",
          region: "",
          currency: COUNTRY_CURRENCY[match.code] || "USD",
          detectedAt: new Date().toISOString(),
          overridden: false,
          detectionMethod: "browser-locale",
        };
      }
      // Try just the country part (e.g., "en" -> US default)
      const langCode = locale.split("-")[1]?.toUpperCase();
      if (langCode && COUNTRY_CURRENCY[langCode]) {
        return {
          country: langCode,
          countryCode: langCode,
          city: "",
          region: "",
          currency: COUNTRY_CURRENCY[langCode],
          detectedAt: new Date().toISOString(),
          overridden: false,
          detectionMethod: "browser-locale",
        };
      }
    }
  } catch {}
  return null;
}

export async function detectLocation(): Promise<GeoData> {
  // Check cache first (skip if stale)
  const cached = loadGeo();
  if (cached && !isStale(cached.detectedAt)) return cached;

  // Try local detection first (no network, no console errors)
  const tzGeo = detectFromTimezone();
  if (tzGeo) {
    saveGeo(tzGeo);
    // Attempt IP enrichment in background (non-blocking, silent)
    enrichFromIp(tzGeo).catch(() => {});
    return tzGeo;
  }

  const localeGeo = detectFromLocale();
  if (localeGeo) {
    saveGeo(localeGeo);
    enrichFromIp(localeGeo).catch(() => {});
    return localeGeo;
  }

  // Only try IP APIs as last resort before final fallback
  const ipGeo = await tryIpDetection();
  if (ipGeo) {
    saveGeo(ipGeo);
    return ipGeo;
  }

  // Use cached even if stale
  if (cached) return cached;

  // Final fallback
  const fallback: GeoData = {
    country: "United States",
    countryCode: "US",
    city: "",
    region: "",
    currency: "USD",
    detectedAt: new Date().toISOString(),
    overridden: false,
    detectionMethod: "browser-locale",
  };
  saveGeo(fallback);
  return fallback;
}

// Silent background IP enrichment to add city/region without blocking
async function enrichFromIp(base: GeoData): Promise<void> {
  const result = await tryIpDetection();
  if (result && result.city) {
    const enriched: GeoData = { ...base, city: result.city, region: result.region };
    saveGeo(enriched);
  }
}

// Attempt IP-based detection, fully silent on failure
async function tryIpDetection(): Promise<GeoData | null> {
  const ipProviders = [
    { url: "https://ipapi.co/json/", parser: (d: any) => ({ country: d.country_name, countryCode: d.country_code, city: d.city, region: d.region, currency: d.currency }) },
    { url: "https://ipwho.is/", parser: (d: any) => ({ country: d.country, countryCode: d.country_code, city: d.city, region: d.region, currency: d.currency?.code }) },
  ];

  for (const provider of ipProviders) {
    try {
      const res = await fetch(provider.url, { signal: AbortSignal.timeout(3000) });
      if (!res.ok) {
        // Consume body to avoid leak, skip silently
        await res.text().catch(() => {});
        continue;
      }
      const data = await res.json();
      const parsed = provider.parser(data);

      if (parsed.countryCode) {
        return {
          country: parsed.country || "United States",
          countryCode: parsed.countryCode || "US",
          city: parsed.city || "",
          region: parsed.region || "",
          currency: parsed.currency || COUNTRY_CURRENCY[parsed.countryCode] || "USD",
          detectedAt: new Date().toISOString(),
          overridden: false,
          detectionMethod: "ip-api",
        };
      }
    } catch {
      continue;
    }
  }
  return null;
}

function isStale(dateStr: string): boolean {
  const diff = Date.now() - new Date(dateStr).getTime();
  return diff > 24 * 60 * 60 * 1000; // 24h
}

export function getLocationLabel(geo: GeoData): string {
  if (geo.city && geo.country) return `${geo.city}, ${geo.country}`;
  return geo.country;
}

export function getCountryFlag(code: string): string {
  const FLAGS: Record<string, string> = {
    US: "🇺🇸", GB: "🇬🇧", CA: "🇨🇦", AU: "🇦🇺", DE: "🇩🇪", FR: "🇫🇷",
    JP: "🇯🇵", IN: "🇮🇳", AE: "🇦🇪", SG: "🇸🇬", BR: "🇧🇷", MX: "🇲🇽",
    NL: "🇳🇱", SE: "🇸🇪", ES: "🇪🇸", IT: "🇮🇹", KR: "🇰🇷", NZ: "🇳🇿",
    IE: "🇮🇪", AT: "🇦🇹", BE: "🇧🇪", FI: "🇫🇮", PT: "🇵🇹", PL: "🇵🇱",
    CH: "🇨🇭", NO: "🇳🇴", DK: "🇩🇰", SA: "🇸🇦", ZA: "🇿🇦",
    HK: "🇭🇰", TW: "🇹🇼", CN: "🇨🇳", ID: "🇮🇩", PH: "🇵🇭",
    TH: "🇹🇭", VN: "🇻🇳", MY: "🇲🇾", TR: "🇹🇷", IL: "🇮🇱",
    CL: "🇨🇱", CO: "🇨🇴", AR: "🇦🇷", PE: "🇵🇪",
  };
  return FLAGS[code] || "🌐";
}

// Map unsupported countries to nearest supported market
const SUPPORTED_CODES = new Set(["US","GB","CA","AU","DE","FR","JP","IN","AE","SG","NL","ES","IT","KR","SE"]);

const REGION_FALLBACK: Record<string, string> = {
  MX: "US", BR: "US", AR: "US", CL: "US", CO: "US", PE: "US",
  CH: "DE", NO: "GB", DK: "GB", FI: "DE", BE: "NL", AT: "DE", PT: "ES", IE: "GB", PL: "DE", RO: "DE", CZ: "DE", HU: "DE",
  HK: "SG", TW: "SG", PH: "SG", TH: "SG", MY: "SG", ID: "SG", VN: "SG", NZ: "AU",
  SA: "AE", QA: "AE", ZA: "GB", NG: "GB", EG: "AE", KE: "GB",
  PK: "IN", BD: "IN", LK: "IN",
  TR: "DE", IL: "GB",
};

export function getNearestSupportedMarket(countryCode: string): { code: string; name: string; isNative: boolean } {
  if (SUPPORTED_CODES.has(countryCode)) {
    const countries = getSupportedCountries();
    const found = countries.find(c => c.code === countryCode);
    return { code: countryCode, name: found?.name || countryCode, isNative: true };
  }
  const fallbackCode = REGION_FALLBACK[countryCode] || "US";
  const countries = getSupportedCountries();
  const found = countries.find(c => c.code === fallbackCode);
  return { code: fallbackCode, name: found?.name || "United States", isNative: false };
}

// Get all supported countries for override selector
export function getSupportedCountries(): { code: string; name: string; flag: string }[] {
  return [
    { code: "US", name: "United States", flag: "🇺🇸" },
    { code: "GB", name: "United Kingdom", flag: "🇬🇧" },
    { code: "CA", name: "Canada", flag: "🇨🇦" },
    { code: "AU", name: "Australia", flag: "🇦🇺" },
    { code: "DE", name: "Germany", flag: "🇩🇪" },
    { code: "FR", name: "France", flag: "🇫🇷" },
    { code: "JP", name: "Japan", flag: "🇯🇵" },
    { code: "IN", name: "India", flag: "🇮🇳" },
    { code: "AE", name: "UAE", flag: "🇦🇪" },
    { code: "SG", name: "Singapore", flag: "🇸🇬" },
    { code: "BR", name: "Brazil", flag: "🇧🇷" },
    { code: "MX", name: "Mexico", flag: "🇲🇽" },
    { code: "NL", name: "Netherlands", flag: "🇳🇱" },
    { code: "SE", name: "Sweden", flag: "🇸🇪" },
    { code: "ES", name: "Spain", flag: "🇪🇸" },
    { code: "IT", name: "Italy", flag: "🇮🇹" },
    { code: "KR", name: "South Korea", flag: "🇰🇷" },
    { code: "NZ", name: "New Zealand", flag: "🇳🇿" },
    { code: "SA", name: "Saudi Arabia", flag: "🇸🇦" },
    { code: "ZA", name: "South Africa", flag: "🇿🇦" },
    { code: "HK", name: "Hong Kong", flag: "🇭🇰" },
  ];
}
